/**
 * useResponseEnhancement Hook
 * Enhances AI responses with animal IDs when suspicious animals are mentioned
 */

import { useCallback } from "react";
import { getSuspiciousAnimals } from "../services/chatContextService";

/**
 * Hook to enhance AI responses with animal details
 * @param {string} userId - User ID
 * @returns {Object} { enhanceResponse } - Function to enhance responses
 */
export const useResponseEnhancement = (userId) => {
        /**
         * Detects if the response mentions listing suspicious animals and enhances it with animal IDs
         * @param {string} response - The AI response text
         * @returns {Promise<string>} Enhanced response with animal IDs if applicable
         */
        const enhanceResponse = useCallback(async (response) => {
                if (!response || !userId) {
                        return response;
                }

                // Detect patterns like "4 Tiere", "4 animals", "Es gibt 4 Tiere", "there are 4 animals", etc.
                // This regex matches numbers (1-9) followed by animal-related terms in multiple languages
                const patterns = [
                        /\b(\d{1,2})\s+(?:Tiere|animals?|animaux)\b/gi, // German/English/French
                        /\b(?:Es gibt|There are|Il y a)\s+(\d{1,2})\s+(?:Tiere|animals?|animaux)\b/gi,
                        /\b(\d{1,2})\s+(?:auffällige|suspicious|suspects?)\s+(?:Tiere|animals?|animaux)\b/gi,
                ];

                let animalCount = null;
                for (const pattern of patterns) {
                        const match = pattern.exec(response);
                        if (match) {
                                // Extract the number from the capturing group
                                const count = parseInt(match[1] || match[0].match(/\d{1,2}/)?.[0], 10);
                                if (count > 0 && count < 10) {
                                        animalCount = count;
                                        break;
                                }
                        }
                }

                // If we detected a small count of animals, fetch and append their IDs
                if (animalCount !== null) {
                        try {
                                const suspiciousAnimals = await getSuspiciousAnimals(userId);
                                
                                // Only enhance if we found animals matching the count (or close to it)
                                if (suspiciousAnimals.length > 0 && suspiciousAnimals.length === animalCount) {
                                        const animalIds = suspiciousAnimals.map(a => a.ID).filter(Boolean);
                                        
                                        if (animalIds.length > 0) {
                                                // Check if IDs are already in the response
                                                const hasIds = animalIds.some(id => response.includes(id));
                                                
                                                if (!hasIds) {
                                                        // Format animal info for the response
                                                        const animalInfo = suspiciousAnimals
                                                                .slice(0, 10)
                                                                .map(a => {
                                                                        const parts = [`ID: ${a.ID}`];
                                                                        if (a.HealthScore !== undefined && a.HealthScore !== null) {
                                                                                parts.push(`HealthScore: ${a.HealthScore.toFixed(1)}`);
                                                                        }
                                                                        if (a.Group && a.Group !== "-") {
                                                                                parts.push(`Group: ${a.Group}`);
                                                                        }
                                                                        return parts.join(", ");
                                                                })
                                                                .join("\n");

                                                        // Append animal details to the response
                                                        const animalDetailsHeader = `\n\n**Animal Details:**\n${animalInfo}`;
                                                        return response + animalDetailsHeader;
                                                }
                                        }
                                }
                        } catch (error) {
                                console.error("[useResponseEnhancement][enhanceResponse] - Error enhancing response:", error);
                        }
                }

                return response;
        }, [userId]);

        return { enhanceResponse };
};

